/**
 * Marketing module client — Stage 3.
 *
 * The Stage 2 reporting endpoints are the ONLY calculation source. Nothing
 * here recomputes a KPI: we request the same report for the current and the
 * previous equivalent period and present the difference.
 */
import api from "@/lib/api";

export const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "last_7", label: "Last 7 days" },
  { key: "last_30", label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "lifetime", label: "Campaign lifetime" },
];

export const DATE_MODES = [
  { key: "acquisition", label: "Acquisition date" },
  { key: "booking", label: "Booking date" },
  { key: "appointment", label: "Appointment date" },
  { key: "revenue", label: "Revenue date" },
];

const iso = (d) => d.toISOString().slice(0, 10);
const shift = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d; };

export function presetRange(key) {
  const today = new Date();
  if (key === "today") return { date_from: iso(today), date_to: iso(today) };
  if (key === "last_7") return { date_from: iso(shift(-6)), date_to: iso(today) };
  if (key === "last_30") return { date_from: iso(shift(-29)), date_to: iso(today) };
  if (key === "this_month") {
    return { date_from: `${iso(today).slice(0, 7)}-01`, date_to: iso(today) };
  }
  if (key === "last_month") {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last = new Date(today.getFullYear(), today.getMonth(), 0);
    return { date_from: iso(first), date_to: iso(last) };
  }
  return { date_from: "", date_to: "" };            // campaign lifetime
}

/** The immediately preceding range of the same length. */
export function priorPeriod({ date_from, date_to }) {
  if (!date_from || !date_to) return null;
  const from = new Date(date_from);
  const to = new Date(date_to);
  const days = Math.round((to - from) / 86400000) + 1;
  const priorTo = new Date(from); priorTo.setDate(priorTo.getDate() - 1);
  const priorFrom = new Date(priorTo); priorFrom.setDate(priorFrom.getDate() - days + 1);
  return { date_from: iso(priorFrom), date_to: iso(priorTo) };
}

export const FILTER_FIELDS = [
  { key: "source", label: "Source", options: "sources" },
  { key: "medium", label: "Medium", options: "mediums" },
  { key: "utm_campaign", label: "utm_campaign", options: "campaigns" },
  { key: "utm_content", label: "Creative (utm_content)", options: "creatives" },
  { key: "suburb", label: "Suburb", options: "suburbs" },
  { key: "postcode", label: "Postcode", options: "postcodes" },
  { key: "technician", label: "Technician", options: "technicians" },
  { key: "health_check_status", label: "Health Check status", options: "health_check_statuses" },
  { key: "owner_present", label: "Owner present", values: ["yes", "no"] },
  { key: "lab_purchased", label: "Lab purchased", values: ["yes", "no"] },
  { key: "lab_package", label: "Lab package", values: ["Tank", "Bore", "4-Point"] },
  { key: "sample_status", label: "Sample status", values: ["to_collect", "collected", "unable_to_collect", "suspended"] },
  { key: "recommendation_type", label: "Recommendation", options: "recommendation_types" },
  { key: "follow_up_status", label: "Follow-up status", options: "follow_up_statuses" },
  { key: "quote_job_status", label: "Quote / job status", options: "quote_job_states" },
];

const clean = (params) => Object.fromEntries(
  Object.entries(params || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined));

export const fetchReport = (params) =>
  api.get("/admin/campaign/report", { params: clean(params) }).then((r) => r.data);
export const fetchFilterOptions = () =>
  api.get("/admin/campaign/report/filters").then((r) => r.data);
export const fetchJourneys = (params) =>
  api.get("/admin/campaign/journeys", { params: clean(params) }).then((r) => r.data);
export const fetchJourney = (reference) =>
  api.get(`/admin/campaign/journey/${reference}`).then((r) => r.data);
export const fetchCampaigns = () =>
  api.get("/admin/marketing/campaigns").then((r) => r.data);
export const fetchCampaign = (id) =>
  api.get(`/admin/marketing/campaigns/${id}`).then((r) => r.data);
export const saveCampaign = (id, body) =>
  api.put(`/admin/marketing/campaigns/${id}`, body).then((r) => r.data);
export const setCampaignStatus = (id, active) =>
  api.post(`/admin/marketing/campaigns/${id}/status`, { active }).then((r) => r.data);
export const fetchSpend = (params) =>
  api.get("/admin/campaign/ad-spend", { params: clean(params) }).then((r) => r.data);
export const createSpend = (body) =>
  api.post("/admin/campaign/ad-spend", body).then((r) => r.data);
export const updateSpend = (id, body) =>
  api.put(`/admin/campaign/ad-spend/${id}`, body).then((r) => r.data);
export const deactivateSpend = (id, reason) =>
  api.delete(`/admin/campaign/ad-spend/${id}`, { params: { reason } }).then((r) => r.data);
export const fetchReconciliation = (params) =>
  api.get("/admin/campaign/ad-spend/reconciliation", { params: clean(params) })
    .then((r) => r.data);

export async function downloadCsv(kind, params) {
  const res = await api.get("/admin/campaign/export", {
    params: { ...clean(params), kind }, responseType: "blob",
  });
  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${kind === "summary" ? "campaign-summary"
    : kind === "creative" ? "creative-performance" : "campaign-journeys"}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const money = (v) =>
  v === null || v === undefined ? null
    : `$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2,
      maximumFractionDigits: 2 })}`;
export const num = (v) =>
  v === null || v === undefined ? null : Number(v).toLocaleString("en-AU");
export const pct = (v) => (v === null || v === undefined ? null : `${v}%`);
export const roas = (v) => (v === null || v === undefined ? null : `${v}x`);

/** Stage 2 reasons rendered as plain English for the tooltip. */
export const REASONS = {
  no_ad_spend: "No ad spend has been entered for this period.",
  no_landing_page_visits: "No landing page visits recorded for this period.",
  no_booked_health_checks: "No Health Checks were booked in this period.",
  no_attended_health_checks: "No Health Checks were attended in this period.",
  no_completed_health_checks: "No Health Checks were completed in this period.",
  no_follow_up_opportunities: "No follow-up opportunities were created.",
  no_quotes_created: "No quotes have been created.",
  creative_spend_unavailable: "No creative-level spend entered — campaign spend is never apportioned.",
  no_creative_spend_recorded: "No creative-level spend entered — campaign spend is never apportioned.",
  geographic_spend_unavailable: "Ad spend cannot be reliably allocated by area.",
  spend_not_allocatable_to_this_breakdown: "Ad spend cannot be reliably allocated to this breakdown.",
  no_ad_spend_recorded_for_campaign: "No ad spend has been entered for this campaign.",
  not_measurable_with_these_filters: "Not measurable with the current customer-level filters.",
  not_connected: "Not connected to AWARE — shown for comparison only.",
};
export const reasonText = (reason) => REASONS[reason] || (reason || "").replace(/_/g, " ");
